import React, { useState, useEffect, useCallback, useRef } from "react";
import { Container, Row, Col, Button, Modal, Form } from "react-bootstrap";
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  EmailIcon,
} from "react-share";
import "./blog.css";
import format from "date-fns/format";
import { useAcubeService } from "../../service/acube-api.hook";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../context/app-context";
import TestimonialCarousel from "../../components/carousels/testimonial-carousel";
import ContactForm from "../../components/forms/contact-form";
import ProductPartners from "../home/sections/product-partners";
import SearchResults from "./search-blog";
import SkeletonScreen from "../../layouts/skeleton";
import axios from "axios";
import SentryTestimonialVideoPlayer from "../../components/videos/sentry-testimonial";
import TestimonialVideoPlayer from "../../components/videos/testimonial-video";

// ---------- Assets ----------
const like =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/thumbs-up-unfilled.svg";
const likeFilled =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/thumbs-up-filled.svg";
const share =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/share-icon.svg";
const search =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/search-btn.svg";
const copyLink =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/link.svg";
const copySuccess =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/copied.svg";
const closeIcon =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/close-menu.svg";

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, "") + "/";
const APP_DOMAIN = process.env.REACT_APP_DOMAIN;

const tags = [
  "Artificial Intelligence",
  "App Development",
  "Hire Developers",
  "Machine Learning",
  "Digital Marketing",
  "Digital Transformation",
  "Data Engineering",
  "Graphic Design",
  "Blockchain",
  "UIUX",
  "IOT",
  "Web 3.0",
];

// Custom debounce hook with cancel function
const useDebounce = (callback, delay) => {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef();

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debouncedFn = useCallback(
    (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    },
    [delay],
  );

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return [debouncedFn, cancel];
};

const BlogComponent = () => {
  // ---------- States ----------
  const [showModal, setShowModal] = useState(false);
  const [currentBlog, setCurrentBlog] = useState(null);
  const [isCopied, setIsCopied] = useState(false);
  const { getProxy, postProxy } = useAcubeService();
  const [blogData, setBlogData] = useState({
    recentBlogs: [],
    mostViewedBlogs: [],
    popularBlogs: [],
  });
  const { ipAddress } = useAppContext();
  const [isSearched, setIsSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const latestSearchRef = useRef(null);
  const isSearchCancelledRef = useRef(false);

  const navigate = useNavigate();

  // ---------- Share ----------
  const shareURL = currentBlog ? `${APP_DOMAIN}/blogs/${currentBlog.slug}` : "";

  const handleShareClick = (blog) => {
    setCurrentBlog(blog);
    setShowModal(true);
    setIsCopied(false);
  };
  const handleCloseModal = () => setShowModal(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareURL);
      setIsCopied(true);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  // ---------- Fetch Blogs ----------
  const fetchBlogs = async () => {
    setIsLoading(true);
    try {
      const result = await getProxy(`blog/limited-blogs/${ipAddress}`);
      setBlogData(result.data || { recentBlogs: [], mostViewedBlogs: [], popularBlogs: [] });
    } catch (err) {
      console.error("Error fetching blogs:", err);
      setBlogData({ recentBlogs: [], mostViewedBlogs: [], popularBlogs: [] });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (ipAddress) fetchBlogs();
  }, [ipAddress]);

  useEffect(() => {
    isSearchCancelledRef.current = false;
  }, [ipAddress]);

  // ---------- Helpers ----------
  const formatDate = (dateString) => format(new Date(dateString), "d MMMM yyyy");

  // const addViewsCount = async (uuid) => {
  //   try {
  //     await postProxy(`blog/views`, { blogUUID: uuid });
  //   } catch (error) {
  //     console.error("Error adding view count:", error);
  //   }
  // };

  const addViewsCount = async (uuid) => {
    const storageKey = `blog-view-${uuid}`;

    // Already counted in this browser
    const hasViewed = localStorage.getItem(storageKey);

    if (hasViewed) {
      return;
    }

    try {
      await postProxy("blog/views", { blogUUID: uuid });

      // Mark as viewed
      localStorage.setItem(storageKey, "true");
    } catch (error) {
      console.error("Error adding view count:", error);
    }
  };

  const handleBlogClick = (blog) => {
    addViewsCount(blog.blog_uuid);
    navigate(`/blogs/${blog.slug}`);
  };

  const handleLikeClick = async (blog) => {
    try {
      const alreadyLiked = blog.likes && blog.likes.length > 0;
      await postProxy(`blog/like`, { blogUUID: blog.blog_uuid, ip: ipAddress });

      setBlogData((prev) => {
        const updated = { ...prev };
        ["recentBlogs", "mostViewedBlogs", "popularBlogs"].forEach((key) => {
          if (Array.isArray(updated[key])) {
            updated[key] = updated[key].map((b) => {
              if (b.blog_uuid === blog.blog_uuid) {
                return alreadyLiked
                  ? { ...b, likesAdded: Math.max(0, b.likesAdded - 1), likes: [] }
                  : { ...b, likesAdded: b.likesAdded + 1, likes: [{}] };
              }
              return b;
            });
          }
        });
        return updated;
      });
    } catch (err) {
      console.log("Error toggling like:", err);
    }
  };

  const handleViewClick = (viewtype) => navigate(`/blogs/viewall/${viewtype}`);

  const handleCategoryClick = (type) => {
    const formattedType = type.trim().replace(/\s+/g, "-").toLowerCase();
    navigate(`/blogs/category/${formattedType}`);
  };

  // ---------- Search ----------
  const performSearch = useCallback(
    async (searchValue) => {
      if (isSearchCancelledRef.current || !searchValue.trim()) {
        setIsLoading(false);
        return;
      }

      latestSearchRef.current = searchValue;

      try {
        const result = await getProxy(`blog/search/${searchValue}/${ipAddress}`);

        if (isSearchCancelledRef.current || latestSearchRef.current !== searchValue) {
          return;
        }

        setBlogData(result.data || []);
        setSelectedTags([]);
      } catch (err) {
        console.error("Error searching blogs:", err);
        if (!isSearchCancelledRef.current) {
          setBlogData([]);
        }
      } finally {
        if (!isSearchCancelledRef.current) {
          setIsLoading(false);
        }
      }
    },
    [getProxy, ipAddress],
  );

  const [debouncedSearch, cancelSearch] = useDebounce(performSearch, 500);

  const handleInputChange = (e) => {
    const { value } = e.target;
    setSearchInput(value);
    setSelectedTags([]);

    isSearchCancelledRef.current = false;

    if (!value.trim()) {
      cancelSearch();
      isSearchCancelledRef.current = true;
      handleSearchClose();
      return;
    }

    setIsSearched(true);
    setIsLoading(true);

    debouncedSearch(value);
  };

  const handleSearchClose = () => {
    cancelSearch();
    isSearchCancelledRef.current = true;

    setSearchInput("");
    setIsSearched(false);
    setSelectedTags([]);
    setIsLoading(false);
    setTimeout(() => {
      isSearchCancelledRef.current = false;
      fetchBlogs();
    }, 0);
  };

  // ---------- Tags ----------
  const fetchBlogsByTags = async (selectedTagsByProps = selectedTags) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}blog/blogs-by-tags`, {
        params: { tags: selectedTagsByProps.join(","), ip: ipAddress },
      });
      setBlogData(response.data.data || {});
      setIsSearched(true);
    } catch (error) {
      console.error("Error fetching blogs by tags:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTagChange = (tag) => {
    setSearchInput("");
    setSelectedTags((prevTags) => {
      const updatedTags = prevTags.includes(tag)
        ? prevTags.filter((t) => t !== tag)
        : [...prevTags, tag];

      if (updatedTags.length) {
        fetchBlogsByTags(updatedTags);
      } else {
        fetchBlogs();
        setIsSearched(false);
      }
      return updatedTags;
    });
  };

  // ---------- Section Renderer ----------
  function renderBlogSection(title, blogs, viewType) {
    return (
      <>
        <div className="d-flex justify-content-between align-items-center mt-5">
          <h1 className="ft-wt-600 mb-0">{title}</h1>
          <Button
            className="p-0 text-primary ft-16"
            variant="link"
            onClick={() => handleViewClick(viewType)}
          >
            View All
          </Button>
        </div>
        <Row className="blog-cards">
          {blogs?.length ? (
            blogs.map((blog) => (
              <Col xs="12" lg="4" key={blog.blog_uuid} className="blog-card cursor-pointer">
                <img
                  src={blog.template_image}
                  alt={blog.title}
                  className="blog-thumbnail"
                  onClick={() => handleBlogClick(blog)}
                />
                <div className="blog-card-info">
                  <div className="blog-meta">
                    <span
                      className="author cursor-pointer"
                      onClick={() => handleCategoryClick(blog.service_category)}
                    >
                      {blog.service_category}
                    </span>
                    <span className="date">{formatDate(blog.created_at)}</span>
                  </div>
                  <h3 className="blog-card-title" onClick={() => handleBlogClick(blog)}>
                    {blog.title}
                  </h3>
                  <p className="blog-content" onClick={() => handleBlogClick(blog)}>
                    {blog.template_content}
                  </p>
                  <div className="blog-actions justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      <img
                        src={blog?.likes?.length ? likeFilled : like}
                        alt="thumbs-up"
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLikeClick(blog);
                        }}
                      />
                      <img
                        src={share}
                        alt="share"
                        style={{ width: "16px" }}
                        className="cursor-pointer"
                        onClick={() => handleShareClick(blog)}
                      />
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <h6 className="mb-0">{blog.likesAdded} likes</h6>
                      <h6 className="mb-0">{blog.views} views</h6>
                    </div>
                  </div>
                </div>
              </Col>
            ))
          ) : (
            <SkeletonScreen count={3} />
          )}
        </Row>
      </>
    );
  }

  // ---------- Render ----------
  return (
    <section className="blog-container">
      <Container fluid className="pb-5 pt-3">
        {/* 🔍 Search */}
        <h1 className="text-primary ft-wt-600 mb-3 mt-0">Blogs</h1>
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search for articles, blogs, etc."
            onChange={handleInputChange}
            value={searchInput}
          />
          <img
            src={isSearched && searchInput ? closeIcon : search}
            alt="search"
            onClick={handleSearchClose}
            className={`${isSearched ? "cursor-pointer" : ""}`}
          />
        </div>

        {/* 🏷 Tags */}
        <div className="tags-container">
          {tags.map((tag, index) => (
            <div
              key={index}
              className={`tag ${selectedTags.includes(tag) ? "active" : ""}`}
              onClick={() => handleTagChange(tag)}
            >
              {tag}
              {selectedTags.includes(tag) && (
                <img
                  src="https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/circle-close.svg"
                  alt="circle-close"
                />
              )}
            </div>
          ))}
        </div>

        {/* 📑 Blog Sections */}
        {isLoading ? (
          <SkeletonScreen />
        ) : isSearched ? (
          <SearchResults
            keyword={searchInput}
            blogs={Array.isArray(blogData) ? blogData : []}
            setBlogData={setBlogData}
            selectedTags={selectedTags}
            fetchBlogsByTags={fetchBlogsByTags}
            isLoading={isLoading}
          />
        ) : (
          <>
            {renderBlogSection("What's New", blogData?.recentBlogs, "recent")}
            {renderBlogSection("Most Viewed", blogData?.mostViewedBlogs, "most-viewed")}
            {renderBlogSection("Popular Posts", blogData?.popularBlogs, "popular")}
          </>
        )}
      </Container>

      {/* 📤 Share Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title className="ft-wt-700">Share This Blog</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3 text-center">
              <Form.Label className="copy-link-label">Share to</Form.Label>
              <div className="d-flex gap-2 justify-content-center">
                <FacebookShareButton url={shareURL}>
                  <FacebookIcon size={48} round />
                </FacebookShareButton>
                <TwitterShareButton url={shareURL}>
                  <TwitterIcon size={48} round />
                </TwitterShareButton>
                <LinkedinShareButton url={shareURL}>
                  <LinkedinIcon size={48} round />
                </LinkedinShareButton>
                <EmailShareButton url={shareURL}>
                  <EmailIcon size={48} round />
                </EmailShareButton>
              </div>
            </Form.Group>
            <Form.Group className="mb-3 position-relative">
              <Form.Label className="copy-link-label">Or copy the link</Form.Label>
              <div className="d-flex align-items-center">
                <Form.Control type="text" value={shareURL} readOnly />
                <Button className="ms-2 p-0 copy-btn" variant="link" onClick={handleCopy}>
                  <img
                    src={isCopied ? copySuccess : copyLink}
                    alt={isCopied ? "copied" : "copy"}
                    className="cursor-pointer"
                  />
                </Button>
              </div>
              {isCopied && <div className="link-success-msg">Link copied successfully!</div>}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="link"
            className="ft-wt-700 text-primary text-decoration-none"
            onClick={handleCloseModal}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 🎥 Testimonials + Contact + Partners */}
      <TestimonialCarousel />
      <Container>
        <Row>
          <Col xs="12" lg="6">
            <TestimonialVideoPlayer />
          </Col>
          <Col xs="12" lg="6">
            <SentryTestimonialVideoPlayer />
          </Col>
        </Row>
      </Container>
      <ContactForm />
      <ProductPartners />
    </section>
  );
};

export default BlogComponent;


// import React, { useState, useEffect, useCallback, useRef } from "react";
// import { Container, Row, Col, Button, Modal, Form, Badge } from "react-bootstrap";
// import {
//   FacebookShareButton,
//   TwitterShareButton,
//   LinkedinShareButton,
//   EmailShareButton,
//   FacebookIcon,
//   TwitterIcon,
//   LinkedinIcon,
//   EmailIcon,
// } from "react-share";
// import format from "date-fns/format";
// import axios from "axios";
// import { useNavigate } from "react-router-dom";

// // Hooks & Context
// import { useAcubeService } from "../../service/acube-api.hook";
// import { useAppContext } from "../../context/app-context";

// // Components
// import TestimonialCarousel from "../../components/carousels/testimonial-carousel";
// import ContactForm from "../../components/forms/contact-form";
// import ProductPartners from "../home/sections/product-partners";
// import SearchResults from "./search-blog";
// import SkeletonScreen from "../../layouts/skeleton";
// import SentryTestimonialVideoPlayer from "../../components/videos/sentry-testimonial";
// import TestimonialVideoPlayer from "../../components/videos/testimonial-video";

// import "./blog.css";

// // ---------- Assets ----------
// const ASSETS = {
//   like: "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/thumbs-up-unfilled.svg",
//   likeFilled: "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/thumbs-up-filled.svg",
//   share: "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/share-icon.svg",
//   search: "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/search-btn.svg",
//   copyLink: "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/link.svg",
//   copySuccess: "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/copied.svg",
//   closeIcon: "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/close-menu.svg",
//   circleClose: "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/circle-close.svg"
// };

// const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, "") + "/";
// const APP_DOMAIN = process.env.REACT_APP_DOMAIN;

// const TAGS = [
//   "Artificial Intelligence", "App Development", "Hire Developers",
//   "Machine Learning", "Digital Marketing", "Digital Transformation",
//   "Data Engineering", "Graphic Design", "Blockchain", "UIUX", "IOT", "Web 3.0"
// ];

// // ---------- Custom Hooks ----------
// const useDebounce = (callback, delay) => {
//   const callbackRef = useRef(callback);
//   const timeoutRef = useRef();

//   useEffect(() => {
//     callbackRef.current = callback;
//   }, [callback]);

//   const debouncedFn = useCallback((...args) => {
//     if (timeoutRef.current) clearTimeout(timeoutRef.current);
//     timeoutRef.current = setTimeout(() => {
//       callbackRef.current(...args);
//     }, delay);
//   }, [delay]);

//   const cancel = useCallback(() => {
//     if (timeoutRef.current) {
//       clearTimeout(timeoutRef.current);
//       timeoutRef.current = null;
//     }
//   }, []);

//   useEffect(() => cancel, [cancel]);

//   return [debouncedFn, cancel];
// };

// // ---------- Sub-Components ----------
// const BlogCard = ({ blog, onBlogClick, onCategoryClick, onLikeClick, onShareClick }) => {
//   const formattedDate = format(new Date(blog.created_at), "d MMM yyyy");
//   const isLiked = blog?.likes?.length > 0;

//   return (
//     <article className="blog-card h-100 d-flex flex-column shadow-sm rounded-4 overflow-hidden bg-white">
//       <div className="blog-thumbnail-wrapper position-relative overflow-hidden cursor-pointer" onClick={() => onBlogClick(blog)}>
//         <img
//           src={blog.template_image || "/fallback-image.jpg"}
//           alt={blog.title}
//           className="blog-thumbnail w-100 object-fit-cover h-auto"
//           style={{ aspectRatio: "16/9" }}
//         />
//       </div>

//       <div className="blog-card-info p-4 pb-2 d-flex flex-column flex-grow-1">
//         <div className="blog-meta d-flex justify-content-between align-items-center mb-2 ft-14 text-muted">
//           <Badge
//             bg="primary"
//             className="author cursor-pointer fw-normal rounded-pill text-white px-3 py-2"
//             onClick={(e) => {
//               e.stopPropagation();
//               onCategoryClick(blog.service_category);
//             }}
//           >
//             {blog.service_category}
//           </Badge>
//           <span className="date">{formattedDate}</span>
//         </div>

//         <h3 className="blog-card-title ft-wt-600 ft-20 mb-3 cursor-pointer" onClick={() => onBlogClick(blog)}>
//           {blog.title}
//         </h3>

//         <p className="blog-content text-secondary mb-4 flex-grow-1 cursor-pointer" onClick={() => onBlogClick(blog)}>
//           {blog.template_content?.length > 120
//             ? `${blog.template_content.substring(0, 120)}...`
//             : blog.template_content}
//         </p>

//         <div className="blog-actions d-flex justify-content-between align-items-center pt-3 border-top">
//           <div className="d-flex align-items-center gap-3">
//             <button className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-1" onClick={() => onLikeClick(blog)}>
//               <img src={isLiked ? ASSETS.likeFilled : ASSETS.like} alt="thumbs-up" width="20" />
//             </button>
//             <button className="btn btn-link p-0" onClick={() => onShareClick(blog)}>
//               <img src={ASSETS.share} alt="share" width="18" />
//             </button>
//           </div>
//           <div className="d-flex align-items-center gap-3 text-muted ft-14">
//             <span><strong>{blog.likesAdded || 0}</strong> likes</span>
//             <span><strong>{blog.views || 0}</strong> views</span>
//           </div>
//         </div>
//       </div>
//     </article>
//   );
// };

// const ShareModal = ({ show, onHide, url, isCopied, onCopy }) => (
//   <Modal show={show} onHide={onHide} centered backdrop="static" contentClassName="rounded-4 border-0 shadow-lg">
//     <Modal.Header closeButton className="border-0 pb-0">
//       <Modal.Title className="ft-wt-700 w-100 text-center">Share This Article</Modal.Title>
//     </Modal.Header>
//     <Modal.Body className="px-4 py-4">
//       <div className="d-flex gap-3 justify-content-center mb-4">
//         <FacebookShareButton url={url}><FacebookIcon size={52} round className="hover-scale" /></FacebookShareButton>
//         <TwitterShareButton url={url}><TwitterIcon size={52} round className="hover-scale" /></TwitterShareButton>
//         <LinkedinShareButton url={url}><LinkedinIcon size={52} round className="hover-scale" /></LinkedinShareButton>
//         <EmailShareButton url={url}><EmailIcon size={52} round className="hover-scale" /></EmailShareButton>
//       </div>

//       <Form.Group className="position-relative">
//         <Form.Label className="text-muted ft-14 fw-semibold mb-2">Or copy link</Form.Label>
//         <div className="d-flex align-items-center bg-light rounded-pill p-1 border">
//           <Form.Control type="text" value={url} readOnly className="border-0 bg-transparent shadow-none ms-3" />
//           <Button variant={isCopied ? "success" : "primary"} className="rounded-pill px-4 d-flex align-items-center gap-2" onClick={onCopy}>
//             <img src={isCopied ? ASSETS.copySuccess : ASSETS.copyLink} alt="copy" width="16" />
//             {isCopied ? "Copied" : "Copy"}
//           </Button>
//         </div>
//       </Form.Group>
//     </Modal.Body>
//   </Modal>
// );

// // ---------- Main Component ----------
// const BlogComponent = () => {
//   const [showModal, setShowModal] = useState(false);
//   const [currentBlog, setCurrentBlog] = useState(null);
//   const [isCopied, setIsCopied] = useState(false);
//   const [isSearched, setIsSearched] = useState(false);
//   const [isLoading, setIsLoading] = useState(false);
//   const [searchInput, setSearchInput] = useState("");
//   const [selectedTags, setSelectedTags] = useState([]);

//   // Mixed data structure handling: objects for split layout, arrays for searches
//   const [blogData, setBlogData] = useState({ recentBlogs: [], mostViewedBlogs: [], popularBlogs: [] });
//   const [searchResults, setSearchResults] = useState([]);

//   const { getProxy, postProxy } = useAcubeService();
//   const { ipAddress } = useAppContext();
//   const navigate = useNavigate();

//   const latestSearchRef = useRef(null);

//   // ---------- Share Logic ----------
//   const shareURL = currentBlog ? `${APP_DOMAIN}/blogs/${currentBlog.slug}` : "";

//   const handleShareClick = (blog) => {
//     setCurrentBlog(blog);
//     setIsCopied(false);
//     setShowModal(true);
//   };

//   const handleCopy = async () => {
//     try {
//       await navigator.clipboard.writeText(shareURL);
//       setIsCopied(true);
//       setTimeout(() => setIsCopied(false), 3000);
//     } catch (err) {
//       console.error("Copy failed:", err);
//     }
//   };

//   // ---------- Data Fetching ----------
//   const fetchBlogs = async () => {
//     setIsLoading(true);
//     try {
//       const result = await getProxy(`blog/limited-blogs/${ipAddress}`);
//       setBlogData(result.data || { recentBlogs: [], mostViewedBlogs: [], popularBlogs: [] });
//     } catch (err) {
//       console.error("Error fetching blogs:", err);
//       setBlogData({ recentBlogs: [], mostViewedBlogs: [], popularBlogs: [] });
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   useEffect(() => {
//     if (ipAddress) fetchBlogs();
//   }, [ipAddress]);

//   // ---------- Interactions ----------
//   const addViewsCount = async (uuid) => {
//     const storageKey = `blog-view-${uuid}`;
//     if (localStorage.getItem(storageKey)) return;

//     try {
//       await postProxy("blog/views", { blogUUID: uuid });
//       localStorage.setItem(storageKey, "true");
//     } catch (error) {
//       console.error("Error adding view count:", error);
//     }
//   };

//   const handleBlogClick = (blog) => {
//     addViewsCount(blog.blog_uuid);
//     navigate(`/blogs/${blog.slug}`);
//   };

//   const handleLikeClick = async (blog) => {
//     try {
//       const alreadyLiked = blog.likes && blog.likes.length > 0;
//       await postProxy(`blog/like`, { blogUUID: blog.blog_uuid, ip: ipAddress });

//       const updateArray = (arr) => {
//         if (!Array.isArray(arr)) return arr;
//         return arr.map((b) => {
//           if (b.blog_uuid === blog.blog_uuid) {
//             return alreadyLiked
//               ? { ...b, likesAdded: Math.max(0, b.likesAdded - 1), likes: [] }
//               : { ...b, likesAdded: b.likesAdded + 1, likes: [{}] };
//           }
//           return b;
//         });
//       };

//       if (isSearched) {
//         setSearchResults(prev => updateArray(prev));
//       } else {
//         setBlogData((prev) => ({
//           recentBlogs: updateArray(prev.recentBlogs),
//           mostViewedBlogs: updateArray(prev.mostViewedBlogs),
//           popularBlogs: updateArray(prev.popularBlogs),
//         }));
//       }
//     } catch (err) {
//       console.error("Error toggling like:", err);
//     }
//   };

//   const handleCategoryClick = (type) => {
//     const formattedType = type.trim().replace(/\s+/g, "-").toLowerCase();
//     navigate(`/blogs/category/${formattedType}`);
//   };

//   // ---------- Search & Tags Logic ----------
//   const performSearch = useCallback(async (searchValue) => {
//     if (!searchValue.trim()) {
//       setIsLoading(false);
//       return;
//     }
//     const currentRequestId = Date.now();
//     latestSearchRef.current = currentRequestId;

//     try {
//       const result = await getProxy(`blog/search/${searchValue}/${ipAddress}`);

//       // Prevent race conditions from older typed keys
//       if (latestSearchRef.current !== currentRequestId) return;

//       // Extract array payload regardless of object wrapping variants
//       let incomingData = [];
//       if (Array.isArray(result)) incomingData = result;
//       else if (result?.data && Array.isArray(result.data)) incomingData = result.data;
//       else if (result?.blogs && Array.isArray(result.blogs)) incomingData = result.blogs;

//       setSearchResults(incomingData);
//       setSelectedTags([]);
//     } catch (err) {
//       console.error("Error searching blogs:", err);
//       if (latestSearchRef.current === currentRequestId) setSearchResults([]);
//     } finally {
//       if (latestSearchRef.current === currentRequestId) setIsLoading(false);
//     }
//   }, [getProxy, ipAddress]);

//   const [debouncedSearch, cancelSearch] = useDebounce(performSearch, 500);

//   const handleInputChange = (e) => {
//     const { value } = e.target;
//     setSearchInput(value);
//     setSelectedTags([]);

//     if (!value.trim()) {
//       handleSearchClose();
//       return;
//     }

//     setIsSearched(true);
//     setIsLoading(true);
//     debouncedSearch(value);
//   };

//   const handleSearchClose = () => {
//     cancelSearch();
//     latestSearchRef.current = null;
//     setSearchInput("");
//     setIsSearched(false);
//     setSelectedTags([]);
//     setSearchResults([]);
//     setIsLoading(false);
//     fetchBlogs();
//   };

//   const fetchBlogsByTags = async (selectedTagsByProps = selectedTags) => {
//     setIsLoading(true);
//     try {
//       const response = await axios.get(`${API_BASE_URL}blog/blogs-by-tags`, {
//         params: { tags: selectedTagsByProps.join(","), ip: ipAddress },
//       });

//       let incomingTagsData = [];
//       const resPayload = response.data;
//       if (Array.isArray(resPayload)) incomingTagsData = resPayload;
//       else if (resPayload?.data && Array.isArray(resPayload.data)) incomingTagsData = resPayload.data;

//       setSearchResults(incomingTagsData);
//       setIsSearched(true);
//     } catch (error) {
//       console.error("Error fetching blogs by tags:", error);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleTagChange = (tag) => {
//     setSearchInput("");
//     setSelectedTags((prevTags) => {
//       const updatedTags = prevTags.includes(tag)
//         ? prevTags.filter((t) => t !== tag)
//         : [...prevTags, tag];

//       if (updatedTags.length) {
//         fetchBlogsByTags(updatedTags);
//       } else {
//         handleSearchClose();
//       }
//       return updatedTags;
//     });
//   };

//   // ---------- Section Layout Renderer ----------
//   const renderBlogSection = (title, blogs, viewType) => {
//     if (!blogs || blogs.length === 0) return null;

//     return (
//       <section className="blog-category-section mt-5 pt-3">
//         <header className="d-flex justify-content-between align-items-end mb-4">
//           <h2 className="ft-wt-700 ft-28 mb-0 text-dark">{title}</h2>
//           <Button
//             variant="link"
//             className="text-primary text-decoration-none fw-semibold ft-16 p-0 d-flex align-items-center gap-1"
//             onClick={() => navigate(`/blogs/viewall/${viewType}`)}
//           >
//             View All <span aria-hidden="true">&rarr;</span>
//           </Button>
//         </header>

//         <Row className="g-4">
//           {blogs.map((blog) => (
//             <Col xs="12" md="6" lg="4" key={blog.blog_uuid}>
//               <BlogCard
//                 blog={blog}
//                 onBlogClick={handleBlogClick}
//                 onCategoryClick={handleCategoryClick}
//                 onLikeClick={handleLikeClick}
//                 onShareClick={handleShareClick}
//               />
//             </Col>
//           ))}
//         </Row>
//       </section>
//     );
//   };

//   return (
//     <main className="blog-page bg-light-gray min-vh-100">
//       <Container fluid="lg" className="py-5">

//         {/* 🔍 Hero & Modern Search Pill Bar */}
//         <section className="text-center mb-5 max-w-800 mx-auto">
//           <h1 className="display-4 ft-wt-700 text-primary mb-3">Our Insights & Blogs</h1>
//           <p className="text-secondary ft-18 mb-4">Discover the latest trends in technology, design, and digital transformation.</p>

//           <div className="search-bar-wrapper position-relative mx-auto shadow-sm rounded-pill overflow-hidden bg-white d-flex align-items-center px-3 border" style={{ maxWidth: '600px' }}>
//             <img src={ASSETS.search} alt="search" width="20" className="opacity-50" />
//             <input
//               type="text"
//               className="form-control border-0 shadow-none py-3 px-3 bg-transparent"
//               placeholder="Search for articles, technologies, or keywords..."
//               onChange={handleInputChange}
//               value={searchInput}
//             />
//             {isSearched && (
//               <button className="btn btn-link p-1 text-muted" onClick={handleSearchClose}>
//                 <img src={ASSETS.closeIcon} alt="clear" width="16" />
//               </button>
//             )}
//           </div>
//         </section>

//         {/* 🏷 Interactive Filter Tags */}
//         <section className="tags-container d-flex flex-wrap justify-content-center gap-2 mb-5">
//           {TAGS.map((tag) => (
//             <Button
//               key={tag}
//               variant={selectedTags.includes(tag) ? "primary" : "outline-secondary"}
//               className={`category-badge rounded-pill px-3 py-2 d-flex align-items-center gap-2 ft-14 transition-all border ${selectedTags.includes(tag) ? 'shadow-sm' : 'bg-white'}`}
//               onClick={() => handleTagChange(tag)}
//             >
//               {tag}
//               {selectedTags.includes(tag) && <img src={ASSETS.circleClose} alt="remove filter" width="14" />}
//             </Button>
//           ))}
//         </section>

//         {/* 📑 Dynamic Display Handling */}
//         {isLoading ? (
//           <SkeletonScreen count={3} />
//         ) : isSearched ? (
//           <SearchResults
//             keyword={searchInput}
//             blogs={searchResults}
//             setBlogData={setSearchResults}
//             selectedTags={selectedTags}
//             fetchBlogsByTags={fetchBlogsByTags}
//             isLoading={isLoading}
//           />
//         ) : (
//           <div className="blog-lists pb-5">
//             {renderBlogSection("What's New", blogData?.recentBlogs, "recent")}
//             {renderBlogSection("Most Viewed", blogData?.mostViewedBlogs, "most-viewed")}
//             {renderBlogSection("Popular Posts", blogData?.popularBlogs, "popular")}
//           </div>
//         )}
//       </Container>

//       {/* 📤 Utility & Engagement Sections */}
//       <ShareModal
//         show={showModal}
//         onHide={() => setShowModal(false)}
//         url={shareURL}
//         isCopied={isCopied}
//         onCopy={handleCopy}
//       />

//       <section className="social-proof bg-white py-5 mt-4 border-top">
//         <TestimonialCarousel />
//         <Container className="mt-5">
//           <Row className="g-4">
//             <Col xs="12" lg="6"><TestimonialVideoPlayer /></Col>
//             <Col xs="12" lg="6"><SentryTestimonialVideoPlayer /></Col>
//           </Row>
//         </Container>
//       </section>

//       <ContactForm />
//       <ProductPartners />
//     </main>
//   );
// };

// export default BlogComponent;